import React, { useMemo, useState } from "react";
import { useGetProfileQuery } from "../../../store/api/authApi";
import type {
	Conversation,
	Message,
} from "../../../store/api/conversationsApi";
import { useGetConversationByIdQuery } from "../../../store/api/conversationsApi";
import type { PropertyViewing } from "../../../store/api/propertyViewingsApi";
import { useListViewingsQuery } from "../../../store/api/propertyViewingsApi";
import { useListUsersQuery } from "../../../store/api/usersApi";
import type { User } from "../../../types";

interface BuyerContextPanelProps {
	conversation: Conversation | { id: string } | string | null;
	onClose: () => void;
}

const BuyerContextPanel: React.FC<BuyerContextPanelProps> = ({
	conversation,
	onClose,
}) => {
	const [activeTab, setActiveTab] = useState("profile");
	const { data: currentUser } = useGetProfileQuery();

	// Fetch full conversation details if we only have ID
	const conversationId =
		typeof conversation === "string"
			? conversation
			: typeof conversation === "object" &&
			  conversation !== null &&
			  "id" in conversation
			? conversation.id
			: null;

	const { data: fullConversation } = useGetConversationByIdQuery(
		conversationId || "",
		{ skip: !conversationId },
	);

	const conversationData: Conversation | null =
		fullConversation ||
		(typeof conversation === "object" &&
		conversation !== null &&
		"participants" in conversation
			? (conversation as Conversation)
			: null);

	// Get buyer from conversation participants (exclude current user)
	const buyerId = useMemo<string | null>(() => {
		if (!conversationData?.participants) return null;
		const participant = conversationData.participants.find(
			(p: string) => p !== currentUser?.id,
		);
		return participant || null;
	}, [conversationData, currentUser]);

	// Fetch buyer user data
	const { data: usersData } = useListUsersQuery(
		{ page_size: 1000 },
		{ skip: !buyerId },
	);

	const buyer = useMemo<User | null>(() => {
		if (!usersData?.results || !buyerId) return null;
		return usersData.results.find((u: User) => u.id === buyerId) || null;
	}, [usersData, buyerId]);

	// Fetch viewings for this buyer by email
	const buyerEmail = buyer?.email;
	const { data: viewingsData } = useListViewingsQuery(
		{ buyer_email: buyerEmail, page_size: 1000 },
		{ skip: !buyerEmail },
	);

	// Build buyer profile from real data
	const buyerProfile = useMemo(() => {
		if (!buyer) {
			return {
				name:
					conversationData && "subject" in conversationData
						? conversationData.subject
						: "Unknown Buyer",
				email: "",
				phone: "",
				location: "",
				budget: "",
				preferences: [],
				agent:
					currentUser?.first_name && currentUser?.last_name
						? `${currentUser.first_name} ${currentUser.last_name}`
						: "",
				joinDate:
					conversationData && "created_at" in conversationData
						? new Date(conversationData.created_at)
						: new Date(),
				totalInquiries: 0,
				viewingsCompleted: 0,
			};
		}

		// Viewings are already filtered by buyer_email in the API
		const buyerViewings = viewingsData?.results || [];

		return {
			name:
				`${buyer.first_name || ""} ${buyer.last_name || ""}`.trim() ||
				buyer.email ||
				"Unknown Buyer",
			email: buyer.email || "",
			phone: buyer.phone || "",
			location: buyer.property_address || "",
			budget: "",
			preferences: [],
			agent:
				currentUser?.first_name && currentUser?.last_name
					? `${currentUser.first_name} ${currentUser.last_name}`
					: "",
			joinDate: buyer.created_at ? new Date(buyer.created_at) : new Date(),
			totalInquiries: 1, // Count conversations
			viewingsCompleted: buyerViewings.filter(
				(v: PropertyViewing) => v.status === "completed",
			).length,
		};
	}, [buyer, conversationData, currentUser, viewingsData]);

	// Property history from viewings (already filtered by buyer_email)
	const propertyHistory = useMemo(() => {
		if (!viewingsData?.results) return [];
		return viewingsData.results
			.map((v: PropertyViewing) => ({
				id: v.id,
				title: v.property_name || "Property",
				location: v.property_address || "",
				image: undefined, // PropertyViewing doesn't have image field
				status: v.status || "scheduled",
				date: v.viewing_date || v.created_at,
			}))
			.sort(
				(a: { date: string }, b: { date: string }) =>
					new Date(b.date).getTime() - new Date(a.date).getTime(),
			);
	}, [viewingsData]);

	// Communication history from conversation messages
	const communicationHistory = useMemo(() => {
		if (
			!conversationData ||
			!("messages" in conversationData) ||
			!conversationData.messages
		)
			return [];
		return conversationData.messages
			.map((msg: Message) => ({
				type: msg.sender === currentUser?.id ? "Outgoing" : "Incoming",
				date: msg.created_at,
				summary: msg.content || "",
			}))
			.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
	}, [conversationData, currentUser]);

	const notes: Array<{ author: string; date: string; content: string }> = []; // Notes feature not implemented yet

	const tabs = [
		{ id: "profile", label: "Profile", icon: "user" },
		{ id: "properties", label: "Properties", icon: "home" },
		{ id: "communication", label: "History", icon: "message-square" },
		{ id: "notes", label: "Notes", icon: "file-text" },
	];

	const renderTabContent = () => {
		switch (activeTab) {
			case "profile":
				return (
					<div className="space-y-6">
						{/* Basic Info */}
						<div>
							<h4 className="font-medium text-foreground mb-3">
								Contact Information
							</h4>
							<div className="space-y-3">
								<div className="flex items-center space-x-3">
									<svg
										className="w-4 h-4 text-muted-foreground"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
										/>
									</svg>
									<span className="text-sm text-foreground">
										{buyerProfile?.name}
									</span>
								</div>
								<div className="flex items-center space-x-3">
									<svg
										className="w-4 h-4 text-muted-foreground"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
										/>
									</svg>
									<span className="text-sm text-muted-foreground">
										{buyerProfile?.email || "No email provided"}
									</span>
								</div>
								<div className="flex items-center space-x-3">
									<svg
										className="w-4 h-4 text-muted-foreground"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
										/>
									</svg>
									<span className="text-sm text-muted-foreground">
										{buyerProfile?.phone || "No phone provided"}
									</span>
								</div>
							</div>
						</div>
						{/* Preferences */}
						<div>
							<h4 className="font-medium text-foreground mb-3">
								Buyer Preferences
							</h4>
							<div className="space-y-2">
								<div className="flex justify-between">
									<span className="text-sm text-muted-foreground">
										Budget Range
									</span>
									<span className="text-sm text-foreground">
										{buyerProfile?.budget || "Not specified"}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-sm text-muted-foreground">
										Preferred Location
									</span>
									<span className="text-sm text-foreground">
										{buyerProfile?.location || "Not specified"}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-sm text-muted-foreground">
										Property Type
									</span>
									<span className="text-sm text-foreground">Any</span>
								</div>
							</div>
						</div>
						{/* Stats */}
						<div>
							<h4 className="font-medium text-foreground mb-3">
								Activity Summary
							</h4>
							<div className="grid grid-cols-2 gap-4">
								<div className="bg-muted/50 rounded-lg p-3 text-center">
									<div className="text-lg font-semibold text-foreground">
										{buyerProfile?.totalInquiries}
									</div>
									<div className="text-xs text-muted-foreground">
										Total Inquiries
									</div>
								</div>
								<div className="bg-muted/50 rounded-lg p-3 text-center">
									<div className="text-lg font-semibold text-foreground">
										{buyerProfile?.viewingsCompleted}
									</div>
									<div className="text-xs text-muted-foreground">Viewings</div>
								</div>
							</div>
						</div>
					</div>
				);

			case "properties":
				return (
					<div className="space-y-4">
						<h4 className="font-medium text-foreground">
							Property Interest History
						</h4>
						{propertyHistory?.length === 0 ? (
							<div className="text-center py-8">
								<div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
									<svg
										className="w-6 h-6 text-muted-foreground"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2H3v2z"
										/>
									</svg>
								</div>
								<p className="text-sm text-muted-foreground">
									No property history available
								</p>
							</div>
						) : (
							<div className="space-y-3">
								{propertyHistory?.map(
									(
										property: {
											id: string;
											title: string;
											location: string;
											image?: string | null;
											status: string;
											date: string;
										},
										index: number,
									) => (
										<div
											key={index}
											className="border border-border rounded-lg p-3"
										>
											<div className="flex items-start space-x-3">
												<img
													src={property?.image || ""}
													alt={property?.title}
													className="w-12 h-12 rounded-lg object-cover"
												/>
												<div className="flex-1">
													<h5 className="font-medium text-sm text-foreground">
														{property?.title}
													</h5>
													<p className="text-xs text-muted-foreground">
														{property?.location}
													</p>
													<div className="flex items-center justify-between mt-2">
														<span className="text-xs text-primary">
															{property?.status}
														</span>
														<span className="text-xs text-muted-foreground">
															{property?.date}
														</span>
													</div>
												</div>
											</div>
										</div>
									),
								)}
							</div>
						)}
					</div>
				);

			case "communication":
				return (
					<div className="space-y-4">
						<h4 className="font-medium text-foreground">
							Communication Timeline
						</h4>
						{communicationHistory?.length === 0 ? (
							<div className="text-center py-8">
								<div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
									<svg
										className="w-6 h-6 text-muted-foreground"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-4.906-1.456L3 21l2.456-5.094A8.959 8.959 0 013 12c0-4.418 3.582-8 8-8s8 3.582 8 8z"
										/>
									</svg>
								</div>
								<p className="text-sm text-muted-foreground">
									No communication history
								</p>
							</div>
						) : (
							<div className="space-y-3">
								{communicationHistory?.map(
									(
										comm: { type: string; date: string; summary: string },
										index: number,
									) => (
										<div
											key={index}
											className="border-l-2 border-primary pl-4 pb-4"
										>
											<div className="flex items-center justify-between mb-1">
												<span className="text-sm font-medium text-foreground">
													{comm?.type}
												</span>
												<span className="text-xs text-muted-foreground">
													{comm?.date}
												</span>
											</div>
											<p className="text-sm text-muted-foreground">
												{comm?.summary}
											</p>
										</div>
									),
								)}
							</div>
						)}
					</div>
				);

			case "notes":
				return (
					<div className="space-y-4">
						<div className="flex items-center justify-between">
							<h4 className="font-medium text-foreground">Agent Notes</h4>
							<button className="text-xs text-primary hover:text-primary/80">
								Add Note
							</button>
						</div>
						{notes?.length === 0 ? (
							<div className="text-center py-8">
								<div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
									<svg
										className="w-6 h-6 text-muted-foreground"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
										/>
									</svg>
								</div>
								<p className="text-sm text-muted-foreground">
									No notes added yet
								</p>
								<button className="text-xs text-primary hover:text-primary/80 mt-2">
									Add first note
								</button>
							</div>
						) : (
							<div className="space-y-3">
								{notes?.map((note, index) => (
									<div key={index} className="bg-muted/30 rounded-lg p-3">
										<div className="flex items-center justify-between mb-2">
											<span className="text-xs font-medium text-foreground">
												{note?.author}
											</span>
											<span className="text-xs text-muted-foreground">
												{note?.date}
											</span>
										</div>
										<p className="text-sm text-foreground">{note?.content}</p>
									</div>
								))}
							</div>
						)}
					</div>
				);

			default:
				return null;
		}
	};

	return (
		<div className="w-80 border-l border-border bg-card flex flex-col h-full">
			{/* Header */}
			<div className="p-4 border-b border-border">
				<div className="flex items-center justify-between">
					<h3 className="font-medium text-foreground">Buyer Information</h3>
					<button
						onClick={onClose}
						className="p-1 hover:bg-muted rounded-full transition-colors"
					>
						<svg
							className="w-4 h-4 text-muted-foreground"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>
			</div>
			{/* Tabs */}
			<div className="border-b border-border">
				<div className="flex">
					{tabs?.map((tab) => (
						<button
							key={tab?.id}
							onClick={() => setActiveTab(tab?.id)}
							className={`flex-1 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
								activeTab === tab?.id
									? "border-primary text-primary"
									: "border-transparent text-muted-foreground hover:text-foreground"
							}`}
						>
							{tab?.label}
						</button>
					))}
				</div>
			</div>
			{/* Content */}
			<div className="flex-1 overflow-y-auto p-4">{renderTabContent()}</div>
			{/* Quick Actions */}
			<div className="p-4 border-t border-border bg-muted/20">
				<div className="space-y-2">
					<button className="w-full bg-primary text-primary-foreground py-2 px-3 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
						Schedule Viewing
					</button>
					<button className="w-full border border-border py-2 px-3 rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors">
						Send Property Links
					</button>
				</div>
			</div>
		</div>
	);
};

export default BuyerContextPanel;
